import { store } from "../store";
import { logout } from "../store/userSlice";
import { handleUnauthorizedError, redirectToLogin } from "./apiMiddleware";

const API_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}/resources`;
const MEMORY_API_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}/memory`;

const disconnect = (): never => {
  store.dispatch(logout());
  localStorage.removeItem('user');
  redirectToLogin();
  throw new Error('Unauthorized');
};

const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  if (!token) {
    disconnect();
  }
  return { Authorization: `Bearer ${token}` };
};

const fetchWithAuth = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const response = await fetch(input, {
    ...(init || {}),
    headers: {
      ...(init && init.headers ? init.headers : {}),
      ...getAuthHeader(),
    },
  });

  if (handleUnauthorizedError(response)) {
    throw new Error('Unauthorized');
  }
  return response;
};

export const getMemoryDebugAccessibleChunks = async (params?: { q?: string }) => {
  const query = new URLSearchParams();
  if (params?.q) {
    query.set('q', params.q);
  }

  const url = `${MEMORY_API_URL}/debug/accessible-chunks${query.toString() ? `?${query}` : ''}`;
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export interface GraphDrive {
  id: string;
  name: string;
  webUrl?: string;
  driveType?: string;
}

export interface GraphDriveItem {
  id: string;
  name: string;
  webUrl?: string;
  size?: number;
  lastModifiedDateTime?: string;
  isFolder: boolean;
  childCount?: number | null;
  mimeType?: string | null;
  downloadUrl?: string | null;
  path?: string;
}

export interface ResourceScopeMenuItem {
  scope: string;
  label: string;
}

export interface ResourceAccessConfig {
  department: string;
  pathsAll?: string[];
  pathsManager2?: string[];
  pathsManager1?: string[];
  // Backward-compat
  scopesAll?: string[];
  scopesManager2?: string[];
  scopesManager1?: string[];
}

export interface ResourceTreeNode {
  id: string;
  name: string;
  path: string;
  hasChildren: boolean;
}

export const getDrives = async (): Promise<GraphDrive[]> => {
  const response = await fetchWithAuth(`${API_URL}/drives`);

  if (!response.ok) {
    throw new Error(`Erreur HTTP: ${response.status}`);
  }

  return response.json();
};

export const searchInScope = async (params: {
  scope: string;
  subPath?: string;
  q: string;
}): Promise<{ items: GraphDriveItem[] }> => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  if (params.subPath) query.set('subPath', params.subPath);
  query.set('q', params.q);

  const response = await fetchWithAuth(`${API_URL}/search?${query.toString()}`);

  if (response.status === 404) {
    return { items: [] };
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const getContentBlob = async (params: {
  scope: string;
  path: string;
  itemId?: string;
  download?: boolean;
}): Promise<{ blob: Blob; contentType: string }> => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  query.set('path', params.path);
  if (params.itemId) query.set('itemId', params.itemId);
  if (params.download) query.set('download', '1');

  const response = await fetchWithAuth(`${API_URL}/content?${query.toString()}`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const blob = await response.blob();
  return { blob, contentType };
};

export const getThumbnailBlob = async (params: {
  scope: string;
  path?: string;
  itemId?: string;
  size?: 'small' | 'medium' | 'large';
}): Promise<{ blob: Blob; contentType: string } | null> => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  if (params.path) query.set('path', params.path);
  if (params.itemId) query.set('itemId', params.itemId);
  if (params.size) query.set('size', params.size);

  const response = await fetchWithAuth(`${API_URL}/thumbnail?${query.toString()}`);

  // No thumbnail available
  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || 'image/jpeg';
  const blob = await response.blob();
  return { blob, contentType };
};

export const getResourceTree = async (params?: {
  parentId?: string;
  parentPath?: string;
}): Promise<ResourceTreeNode[]> => {
  const query = new URLSearchParams();
  if (params?.parentId) query.set('parentId', params.parentId);
  if (params?.parentPath) query.set('parentPath', params.parentPath);

  const response = await fetchWithAuth(`${API_URL}/tree${query.toString() ? `?${query.toString()}` : ''}`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const getDepartments = async (): Promise<string[]> => {
  const response = await fetchWithAuth(`${API_URL}/departments`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const getAccessConfigs = async (): Promise<ResourceAccessConfig[]> => {
  const response = await fetchWithAuth(`${API_URL}/access-config`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const updateAccessConfig = async (params: {
  department: string;
  pathsAll: string[];
  pathsManager2: string[];
  pathsManager1: string[];
}): Promise<ResourceAccessConfig> => {
  const response = await fetchWithAuth(`${API_URL}/access-config/${encodeURIComponent(params.department)}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      pathsAll: params.pathsAll,
      pathsManager2: params.pathsManager2,
      pathsManager1: params.pathsManager1,
    }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const getMenu = async (): Promise<ResourceScopeMenuItem[]> => {
  const response = await fetchWithAuth(`${API_URL}/menu`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const listByScope = async (params: {
  scope: string;
  subPath?: string;
}): Promise<{ items: GraphDriveItem[] }> => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  if (params.subPath) query.set('subPath', params.subPath);

  const response = await fetchWithAuth(`${API_URL}/list?${query.toString()}`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};

export const getFile = async (params: {
  scope: string;
  path: string;
}): Promise<GraphDriveItem> => {
  const query = new URLSearchParams();
  query.set('scope', params.scope);
  query.set('path', params.path);

  const response = await fetchWithAuth(`${API_URL}/file?${query.toString()}`);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Erreur HTTP: ${response.status} ${text}`);
  }

  return response.json();
};
